import { CheckboxCard } from "@/src/components/cards";
import { openDrawer } from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import { Ordonnance, medicineHoraires } from "@/src/data/allTypes";
import { jsonAbstract, uid } from "@/src/utils/utils";
import { useState } from "react";
import { FaArrowRightLong } from "react-icons/fa6";

async function openPosologyCalculation({
  ordonnance,
  onConfirm,
}: {
  ordonnance: Ordonnance;
  onConfirm: (ordonnance: Ordonnance) => void;
}) {
  if (!getCalculationFields({ ordonnance }).length)
    return onConfirm(ordonnance);

  await openDrawer({
    title: "Calculer les posologies",
    content: (
      <PosologyCalculationDrawer
        current={ordonnance}
        onChange={(newValue) => {
          ordonnance = newValue;
        }}
      ></PosologyCalculationDrawer>
    ),
    onConfirm: async () => {
      ordonnance = await openPosologyPreviewDrawer({ ordonnance, onConfirm });
    },
  });

  return ordonnance;
}

async function openPosologyPreviewDrawer({
  ordonnance,
  onConfirm,
}: {
  ordonnance: Ordonnance;
  onConfirm: (ordonnance: Ordonnance) => void;
}) {
  let excludedIndexes: number[] = [];

  await openDrawer({
    title: "Confirmer Nouvelles posologies",

    content: (
      <PosologyCalculationsPreview
        oldOrdonnance={ordonnance}
        ordonnance={calculateAutomaticPosologies({
          ordonnance: jsonAbstract(ordonnance),
        })}
        onExcludedIndexesChange={(newValue) => {
          excludedIndexes = newValue;
        }}
      ></PosologyCalculationsPreview>
    ),
    onConfirm: async () => {
      ordonnance = calculateAutomaticPosologies({
        ordonnance,
        excludedIndexes,
      });
      await onConfirm(ordonnance);
    },
  });

  return ordonnance;
}

function calculateAutomaticPosologies({
  ordonnance,
  excludedIndexes,
}: {
  ordonnance: Ordonnance;
  excludedIndexes?: number[];
}) {
  const { weight, height, surface } = ordonnance;

  let i = 0;
  for (let ordonnanceMedicine of ordonnance.ordonnanceMedicines) {
    if (excludedIndexes?.includes(i)) continue;
    if (!ordonnanceMedicine) continue;
    for (let intake of ordonnanceMedicine.intakes) {
      if (!intake) continue;
      let { unit, posology } = intake;
      posology ||= 0;

      if (unit == "mg/kg" && weight) {
        intake.posology = Math.round(parseFloat(posology?.toString()) * weight);
        intake.unit = "mg";
      }

      if (unit == "mg/m2" && surface) {
        intake.posology = Math.round(
          parseFloat(posology?.toString()) * surface
        );
        intake.unit = "mg";
      }
    }
    i++;
  }
  return ordonnance;
}

function getCalculationFields({ ordonnance }: { ordonnance: Ordonnance }) {
  let fields: ("weight" | "surface")[] = [];
  for (let ordonnanceMedicine of ordonnance.ordonnanceMedicines) {
    for (let intake of ordonnanceMedicine?.intakes || []) {
      if (!intake) continue;
      if (intake.unit.includes("/kg")) fields.push("weight");
      if (intake.unit.includes("/m2")) fields.push("surface");
    }
  }
  return fields;
}

function PosologyCalculationDrawer({
  current,
  onChange,
}: {
  current: Ordonnance;
  onChange: (newValue: Ordonnance) => void;
}) {
  const [calculatedReference, setCalculatedReference] = useState<
    "surface" | null
  >(null);
  const [ordonnance, setOrdonnance] = useState(current);

  let fields = getCalculationFields({ ordonnance });

  function onValueChange(ordonnance: Ordonnance) {
    setOrdonnance({ ...ordonnance });
    onChange({ ...ordonnance });
  }
  if (fields.length == 0) return <div></div>;

  function calculateSurface() {
    let { height, weight, surface } = ordonnance;
    if (!height || !weight) return surface;
    if (height < 10) return surface;
    setCalculatedReference("surface");

    let newSurface = Math.sqrt((height * weight) / 3600);
    //rounde to 3 decimal
    return Math.round(newSurface * 1000) / 1000;
  }

  return (
    <div className="c1 gap10">
      <Input
        name="Poids (kg)"
        value={ordonnance?.weight}
        type="number"
        onChange={(newValue) => {
          ordonnance.weight = newValue;
          ordonnance.surface = calculateSurface();
          onValueChange(ordonnance);
        }}
      />

      <Input
        name="Taille (cm)"
        value={ordonnance?.height}
        type="number"
        onChange={(newValue) => {
          ordonnance.height = newValue;
          ordonnance.surface = calculateSurface();
          onValueChange(ordonnance);
        }}
      ></Input>

      <Input
        key={`surface${calculatedReference == "surface" ? uid() : ""}`}
        name="Surface corporelle"
        value={ordonnance?.surface}
        type="number"
        onChange={(newValue) => {
          setCalculatedReference(null);
          ordonnance.surface = newValue;
          onValueChange(ordonnance);
        }}
      ></Input>
    </div>
  );
}

function PosologyCalculationsPreview({
  oldOrdonnance,
  ordonnance,
  currentExcludedIndexes,
  onExcludedIndexesChange,
}: {
  oldOrdonnance: Ordonnance;
  ordonnance: Ordonnance;
  currentExcludedIndexes?: number[];
  onExcludedIndexesChange?: (newValue: number[]) => void;
}) {
  let [excludedIndexes, setExcludedIndexes] = useState(
    currentExcludedIndexes || []
  );

  return (
    <div className="c1 gap10">
      {ordonnance.ordonnanceMedicines?.map((ordonnanceMedicine, index) => {
        const oldOrdonnanceMedicine = oldOrdonnance.ordonnanceMedicines[index];
        if (
          JSON.stringify(ordonnanceMedicine) ==
          JSON.stringify(oldOrdonnanceMedicine)
        ) {
          return;
        }

        let isExcluded = excludedIndexes.includes(index);

        let medicine = ordonnanceMedicine?.medicine;
        if (!medicine) return;

        return (
          <CheckboxCard
            key={uid()}
            color="blue"
            title={
              <div>
                <span className="bold">
                  {[
                    medicine.name,
                    medicine.dosage || null,
                    medicine.form || null,
                  ]
                    .filter((e) => e != null)
                    .join(" - ")}
                </span>
                <span className="gray">
                  - {ordonnanceMedicine?.medicine?.dci}
                </span>
              </div>
            }
            status={isExcluded ? "inactive" : "active"}
            onClick={() => {
              if (isExcluded) {
                excludedIndexes = excludedIndexes.filter((e) => e != index);
              } else {
                excludedIndexes.push(index);
              }

              setExcludedIndexes([...excludedIndexes]);
              onExcludedIndexesChange?.([...excludedIndexes]);
            }}
          >
            <div>
              <div className="c1 gap5">
                {ordonnanceMedicine?.intakes?.map((intake, subIndex) => {
                  if (!intake) return;
                  const oldIntake =
                    oldOrdonnance.ordonnanceMedicines[index]?.intakes[subIndex];

                  let horaire = medicineHoraires[intake?.horaire];
                  return (
                    <div key={uid()} className="c3 gap10">
                      <div className="gray">
                        {oldIntake?.posology || ""} {oldIntake?.unit}{" "}
                        {horaire?.name}
                      </div>
                      <div className="center">
                        <FaArrowRightLong size={18} />
                      </div>
                      <div className="bold main-color tright">
                        {intake?.posology || ""} {intake?.unit} {horaire?.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CheckboxCard>
        );
      })}
    </div>
  );
}

export { openPosologyCalculation };
